import { inject, injectable } from "inversify";
import { Logger, type PointerPosition } from "@designboticteam/core";
import { CursorSnappingService } from "../../../../input/CursorSnappingService";
import { Workspace3DStoreService } from "../../../../services/Workspace3DStoreService";
import { SceneService } from "../../../../stage/SceneService";
import { Polygon } from "../../../entities/Polygon";
import type { Polyline } from "../../../entities/Polyline";
import { ShapeEditingService } from "../../../services/ShapeEditingService";
import { PolylineTool } from "../PolylineTool/PolylineTool";

const MINIMUM_POINT_COUNT = 3;
const FIRST_POINT_SNAP_TOLERANCE_PX = 15;

@injectable()
class PolygonTool extends PolylineTool {
	public static readonly toolName: string = "PolygonTool";
	public override readonly toolName: string = PolygonTool.toolName;
	protected override readonly logger = new Logger(`⬠ ${this.toolName}`, {
		color: "#ffffff",
		backgroundColor: "#4b5563",
	});

	constructor(
		@inject(ShapeEditingService) protected readonly shapeEditingService: ShapeEditingService,
		@inject(SceneService) protected readonly sceneService: SceneService,
		@inject(CursorSnappingService) protected readonly cursorSnappingService: CursorSnappingService,
		@inject(Workspace3DStoreService) protected readonly workspace3DStoreService: Workspace3DStoreService,
	) {
		super(shapeEditingService, sceneService, cursorSnappingService, workspace3DStoreService);
	}

	public override onTap(position: PointerPosition): void {
		if (this.currentShape !== null) {
			const { screenPosition } = this.cursorSnappingService.pickPosition();
			const points = this.currentShape.getPoints();
			const firstPoint = points[0];

			if (screenPosition !== null && firstPoint !== undefined) {
				const firstPointScreen = this.cursorSnappingService.getPositionOnViewport(firstPoint);

				if (firstPointScreen !== null) {
					const dx = screenPosition.x - firstPointScreen.x;
					const dy = screenPosition.y - firstPointScreen.y;
					const distanceSq = dx * dx + dy * dy;

					if (distanceSq < FIRST_POINT_SNAP_TOLERANCE_PX * FIRST_POINT_SNAP_TOLERANCE_PX) {
						if (points.length < MINIMUM_POINT_COUNT) {
							this.discardCurrentShape();
						} else {
							this.acceptCurrentShape();
						}

						return;
					}
				}
			}
		}

		super.onTap(position);
	}

	public override onDoubleTap(_position: PointerPosition): void {
		if (this.currentShape === null) {
			return;
		}

		if (this.currentShape.getPoints().length < MINIMUM_POINT_COUNT) {
			return;
		}

		this.acceptCurrentShape();
	}

	protected override createShape(): Polyline {
		return new Polygon({ label: "Polygon" });
	}
}

export { PolygonTool };
